import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  PartyPopper, Send, Mail, Bell, ShieldCheck, Clock, CheckCircle2, X, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STAGE_KEY_PREFIX = "cq_disputes_prompt_dismissed_";

/**
 * "Your Disputes Have Been Submitted" popup — fires the FIRST time a client
 * lands on their dashboard after their status is set to "Disputes Submitted".
 * If the client has already answered (server-side flag `dispute_response_at`)
 * we never show it again. Otherwise it stays lightly persistent per session:
 * dismissing without answering shows again on the next login.
 */
export default function DisputesSubmittedPrompt({ status, clientId, alreadyResponded }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("intro"); // intro | note | done
  const [received, setReceived] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const key = clientId ? `${STAGE_KEY_PREFIX}${clientId}` : null;

  useEffect(() => {
    if (status !== "Disputes Submitted") return;
    if (alreadyResponded) return;
    if (!key) return;
    if (sessionStorage.getItem(key) === "1") return;
    // Small delay so the dashboard renders behind the modal
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, [status, alreadyResponded, key]);

  const dismiss = () => {
    if (key) sessionStorage.setItem(key, "1");
    setOpen(false);
  };

  const submit = async (didReceive) => {
    setBusy(true);
    try {
      await api.post("/client/dispute-response", {
        received: !!didReceive,
        stage: "Disputes Submitted",
        note: note.trim() || undefined,
      });
      toast.success(didReceive
        ? "Thanks — your specialist has been alerted."
        : "Got it. We'll keep watching for you.");
      setPhase("done");
      // Server will now return alreadyResponded=true; this is the last time we show it.
      if (key) sessionStorage.setItem(key, "1");
      setTimeout(() => setOpen(false), 1600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your response.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="disputes-submitted-prompt">
        {phase === "intro" && (
          <>
            <DialogHeader>
              <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center shadow-lg shadow-emerald-200 mb-2">
                <Send size={26} strokeWidth={2.5} />
              </div>
              <DialogTitle className="text-center text-2xl font-display font-black text-slate-900">
                Your Disputes Have Been Submitted <span aria-hidden>🎉</span>
              </DialogTitle>
              <DialogDescription className="text-center text-slate-600 mt-1 text-[15px] leading-relaxed">
                Your file has officially moved into the dispute process. Credit Quack has prepared and submitted disputes related to the items identified during your credit analysis.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-2 rounded-2xl bg-slate-50 border border-slate-100 p-4">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Where we submit</p>
              <ul className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-sm text-slate-700">
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> Experian</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> Equifax</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> TransUnion</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> Better Business Bureau</li>
                <li className="flex items-center gap-2 col-span-2"><CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> Consumer Financial Protection Bureau</li>
              </ul>
            </div>

            <p className="text-[13px] text-slate-500 leading-relaxed mt-3">
              Our process uses applicable consumer protections, including rights provided under the <b>Fair Credit Reporting Act (FCRA)</b>, to challenge information that may be inaccurate, incomplete, unverifiable, or otherwise subject to dispute. Where appropriate, correspondence may also be sent using expedited or trackable USPS mail so we can document delivery and maintain a clear paper trail.
            </p>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={16} className="text-emerald-600" />
                  <p className="font-bold text-slate-900">What happens now</p>
                </div>
                <p className="text-[13px] text-slate-600 leading-relaxed">
                  We monitor for responses on a <b>14-day checkpoint</b>. Some disputes and investigations can take up to <b>30 days or longer</b> depending on the circumstances — results aren't always immediate.
                </p>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Bell size={16} className="text-amber-600" />
                  <p className="font-bold text-slate-900">We need your help</p>
                </div>
                <p className="text-[13px] text-slate-600 leading-relaxed">
                  Please watch your <span className="inline-flex items-center gap-1"><Mail size={12} /> physical mail</span>, <span className="inline-flex items-center gap-1">📧 email</span>, and <span className="inline-flex items-center gap-1">📱 credit monitoring alerts</span>. If ANY correspondence, response, investigation result, updated credit report, or notice arrives from a bureau, creditor, collector, the CFPB, BBB, or another involved party — tell us.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border-2 border-emerald-200 bg-white p-4">
              <p className="text-center text-lg font-display font-black text-slate-900">
                Have you received any responses yet?
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mt-3 justify-center">
                <Button
                  onClick={() => { setReceived(true); setPhase("note"); }}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold shadow-md shadow-emerald-200"
                  data-testid="disputes-response-yes"
                >
                  ✅ YES, I RECEIVED SOMETHING
                </Button>
                <Button
                  onClick={() => { setReceived(false); submit(false); }}
                  variant="outline"
                  className="border-slate-200 font-bold"
                  disabled={busy}
                  data-testid="disputes-response-no"
                >
                  🕓 NO, NOT YET
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 text-center mt-2">
                Your answer is sent straight to your assigned specialist.
              </p>
            </div>

            <DialogFooter className="pt-3">
              <button
                onClick={dismiss}
                className="text-xs text-slate-400 hover:text-slate-600 mx-auto flex items-center gap-1"
                data-testid="disputes-remind-later"
              >
                <X size={12} /> Remind me later
              </button>
            </DialogFooter>
          </>
        )}

        {phase === "note" && (
          <>
            <DialogHeader>
              <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-lg shadow-amber-200 mb-2">
                <Mail size={26} strokeWidth={2.5} />
              </div>
              <DialogTitle className="text-center text-2xl font-display font-black text-slate-900">
                Tell us what you got
              </DialogTitle>
              <DialogDescription className="text-center text-slate-600 mt-1 text-[15px]">
                A quick note is enough — your specialist will follow up. If you can attach a photo of the letter later inside <b>Messages</b>, that helps too.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={note} onChange={(e) => setNote(e.target.value)} rows={5}
              placeholder="e.g. Received a letter from Experian dated Feb 12 about my Capital One account…"
              className="mt-2" data-testid="disputes-response-note"
            />
            <DialogFooter className="mt-3 flex flex-col sm:flex-row gap-2 sm:justify-between">
              <Button
                variant="outline"
                onClick={() => setPhase("intro")}
                className="border-slate-200"
                disabled={busy}
              >
                Back
              </Button>
              <Button
                onClick={() => submit(true)}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold"
                disabled={busy}
                data-testid="disputes-response-submit"
              >
                {busy ? <><Loader2 className="animate-spin mr-1" size={14} /> Sending…</> : <><Send size={14} className="mr-1" /> Alert my specialist</>}
              </Button>
            </DialogFooter>
          </>
        )}

        {phase === "done" && (
          <div className="text-center py-6">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center shadow-lg shadow-emerald-200 mb-3">
              <PartyPopper size={30} strokeWidth={2.5} />
            </div>
            <p className="text-2xl font-display font-black text-slate-900">You're all set</p>
            <p className="text-slate-600 mt-1">Your specialist has been alerted.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
